'use client';

import { Image as ImageIcon, Loader2, Upload, X } from 'lucide-react';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SetupStoreFormRow } from '@/components/setup/experience/SetupStoreFormRow';
import { useUploadImage } from '@/lib/api/images';
import { cn } from '@/lib/utils';

const ACCEPTED_IMAGE_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export function SetupStoreLogoField() {
  const { t } = useTranslation('setup');
  const uploadImageMutation = useUploadImage();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<{ logoUrl: string }>();

  const logoUrl = watch('logoUrl');
  const hasLogo = Boolean(logoUrl?.trim());
  const isUploading = uploadImageMutation.isPending;
  const errorMessage = errors.logoUrl?.message;

  const uploadFile = (file: File) => {
    if (!ACCEPTED_IMAGE_MIME.includes(file.type)) return;
    const formData = new FormData();
    formData.append('file', file);
    uploadImageMutation.mutate(formData, {
      onSuccess: (data) => setValue('logoUrl', data.file.url, { shouldDirty: true }),
    });
  };

  const openFilePicker = () => {
    if (!isUploading) inputRef.current?.click();
  };

  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    setValue('logoUrl', '', { shouldDirty: true });
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(event.type === 'dragenter' || event.type === 'dragover');
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file && !isUploading) uploadFile(file);
  };

  return (
    <SetupStoreFormRow
      label={t('experience.connect.storeForm.logoLabel')}
      optionalLabel={t('experience.connect.storeForm.logoOptional')}
      icon={ImageIcon}
      hint={t('experience.connect.storeForm.logoHint')}
      error={errorMessage}
    >
      <div className="flex justify-end">
        <div
          className="relative size-20 shrink-0"
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <button
            type="button"
            onClick={openFilePicker}
            disabled={isUploading}
            aria-label={
              hasLogo
                ? t('experience.connect.storeForm.logoReplace')
                : t('experience.connect.storeForm.logoUpload')
            }
            className={cn(
              'group/slot relative flex size-full cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-[color-mix(in_oklch,var(--border)_90%,transparent)] bg-[color-mix(in_oklch,var(--muted)_35%,var(--card))] p-0 transition-[border-color,background] duration-150 hover:enabled:border-[var(--crm-selection-border)] hover:enabled:bg-[color-mix(in_oklch,var(--primary)_6%,var(--background))]',
              hasLogo && 'border-solid',
              isDragActive && 'border-[var(--crm-selection-border)] bg-[color-mix(in_oklch,var(--primary)_6%,var(--background))]',
              isUploading && 'cursor-wait',
            )}
          >
            {isUploading ? (
              <span className="text-primary flex items-center justify-center" role="status">
                <Loader2 className="size-5 animate-spin" aria-hidden />
                <span className="sr-only">{t('experience.connect.storeForm.logoUploading')}</span>
              </span>
            ) : hasLogo ? (
              <>
                <img src={logoUrl} alt="" className="block size-full object-cover" draggable={false} />
                <span className="absolute inset-x-0 bottom-0 bg-[linear-gradient(to_top,rgb(0_0_0_/_0.72),transparent)] px-1 py-1 text-center text-[0.625rem] font-semibold uppercase tracking-[0.02em] text-white opacity-0 transition-opacity duration-150 group-hover/slot:opacity-100">
                  {t('experience.connect.storeForm.logoReplace')}
                </span>
              </>
            ) : (
              <span className="flex items-center justify-center text-[color-mix(in_oklch,var(--muted-foreground)_82%,transparent)]">
                <Upload className="size-5" strokeWidth={2} aria-hidden />
              </span>
            )}
          </button>

          {hasLogo && !isUploading ? (
            <button
              type="button"
              onClick={handleRemove}
              aria-label={t('experience.connect.storeForm.logoRemove')}
              className="absolute right-[-0.3125rem] top-[-0.3125rem] z-[2] flex size-[1.375rem] cursor-pointer items-center justify-center rounded-full border border-[color-mix(in_oklch,var(--background)_70%,transparent)] bg-[color-mix(in_oklch,var(--foreground)_78%,#000)] p-0 text-white shadow-[0_1px_4px_rgb(0_0_0_/_0.28)] transition-[transform,background] duration-150 hover:scale-105 hover:bg-[color-mix(in_oklch,var(--destructive)_82%,#000)]"
            >
              <X className="size-3.5" strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_IMAGE_MIME.join(',')}
            className="sr-only"
            onInput={(event) => {
              const file = (event.target as HTMLInputElement).files?.item(0);
              if (file) uploadFile(file);
              (event.target as HTMLInputElement).value = '';
            }}
          />
        </div>
      </div>

      {uploadImageMutation.isError ? (
        <p className="text-destructive m-0 mt-2 text-xs sm:text-right" role="alert">
          {t('experience.connect.storeForm.logoUploadFailed')}
        </p>
      ) : null}
    </SetupStoreFormRow>
  );
}
